/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../../__generated__/zeus/types';

import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/** Progress through the home assessment stage. */
export type SellerAssessmentJourneyStatus =
  /** An assessment is underway or awaiting review. */
  | 'IN_PROGRESS'
  /** The seller has not started or scheduled an assessment. */
  | 'NOT_STARTED'
  /** The assessment is complete and the seller can review the offer. */
  | 'READY_FOR_REVIEW'
  /** An assessment is scheduled. */
  | 'SCHEDULED';

/** Next action in the closing stage. */
export type SellerClosingJourneyStatus =
  /** The seller must sign an outstanding addendum. */
  | 'ADDENDUM_SIGNATURE_REQUIRED'
  /** The seller must complete the final checkout. */
  | 'CHECKOUT_REQUIRED'
  /** The seller must complete property disclosures. */
  | 'DISCLOSURES_REQUIRED'
  /** The seller must provide utility and home information. */
  | 'HOME_INFORMATION_REQUIRED'
  /** A post-contract inspection is scheduled or in review. */
  | 'INSPECTION_IN_PROGRESS'
  /** The seller must schedule a post-contract inspection. */
  | 'INSPECTION_SCHEDULING_REQUIRED'
  /** The seller has no outstanding closing action. */
  | 'NO_ACTION_REQUIRED';

/** Next action in the purchase agreement stage. */
export type SellerContractJourneyStatus =
  /** The purchase agreement is complete and ready to review. */
  | 'READY_FOR_REVIEW'
  /** The seller must complete contract setup. */
  | 'SETUP_REQUIRED'
  /** The seller must sign the purchase agreement. */
  | 'SIGNATURE_REQUIRED';

/** A selling or home-value product associated with a seller lead. */
export type SellerProduct =
  /** Connect the seller with a real estate agent through a partner referral. */
  | 'AGENT_REFERRAL'
  /** Buy another home through Opendoor while selling the current home. */
  | 'BUY_WITH_OPENDOOR'
  /** Sell through an agent outside the Opendoor partner network. */
  | 'EXTERNAL_LISTING_AGENT'
  /** Receive home-value and property insights without a selling offer. */
  | 'HOME_INSIGHTS'
  /** List the home for sale with Opendoor support. */
  | 'LIST_WITH_OPENDOOR'
  /** No seller product is available for the property. */
  | 'NO_PRODUCT'
  /** Use an Opendoor-backed offer to support a home purchase. */
  | 'OPENDOOR_BACKED_OFFERS'
  /** Sell the home directly to Opendoor for a cash offer. */
  | 'OPENDOOR_DIRECT'
  /** Sell the home through an institutional real-estate program. */
  | 'REIT'
  /** Sell the home on the MLS with help from Opendoor. */
  | 'SELLER_LISTING'
  /** Sell through an Opendoor exclusive-offers experience. */
  | 'SELL_WITH_EXCLUSIVES'
  /** Receive an upfront payment while sharing in future resale upside. */
  | 'SELL_WITH_UPSIDE';

export type HubSellerTransactionsQueryVariables = Exact<{ [key: string]: never; }>;


export type HubSellerTransactionsQuery = { customerAccount: { seller: { transactions: Array<
        | { __typename: 'ClosedTransaction', selectedProduct: Types.SellerProduct | null, id: Types.Scalars['ID']['output'], homeResale:
            | { __typename: 'ListedHomeResale' }
            | { __typename: 'PreparingHomeResale' }
            | { __typename: 'SoldHomeResale' }
           | null, address: { id: Types.Scalars['ID']['output'], street: Types.Scalars['String']['output'], unitNumber: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'], state: Types.Scalars['String']['output'], postalCode: Types.Scalars['PostalCode']['output'], latitude: Types.Scalars['Latitude']['output'] | null, longitude: Types.Scalars['Longitude']['output'] | null }, application: { homeDetails: { physicalDetails: { bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, bathroomsFull: Types.Scalars['SafeInt']['output'] | null, bathroomsHalf: Types.Scalars['SafeInt']['output'] | null, sqFt: Types.Scalars['SafeInt']['output'] | null } | null } | null } | null }
        | { __typename: 'DeniedTransaction', id: Types.Scalars['ID']['output'], address: { id: Types.Scalars['ID']['output'], street: Types.Scalars['String']['output'], unitNumber: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'], state: Types.Scalars['String']['output'], postalCode: Types.Scalars['PostalCode']['output'], latitude: Types.Scalars['Latitude']['output'] | null, longitude: Types.Scalars['Longitude']['output'] | null }, application: { homeDetails: { physicalDetails: { bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, bathroomsFull: Types.Scalars['SafeInt']['output'] | null, bathroomsHalf: Types.Scalars['SafeInt']['output'] | null, sqFt: Types.Scalars['SafeInt']['output'] | null } | null } | null } | null }
        | { __typename: 'InContractTransaction', id: Types.Scalars['ID']['output'], currentStep:
            | { __typename: 'SellerJourneyClosingStep', closingStatus: Types.SellerClosingJourneyStatus | null }
            | { __typename: 'SellerJourneyRepricingStep', repricingStatus: Types.SellerContractJourneyStatus | null }
           | null, address: { id: Types.Scalars['ID']['output'], street: Types.Scalars['String']['output'], unitNumber: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'], state: Types.Scalars['String']['output'], postalCode: Types.Scalars['PostalCode']['output'], latitude: Types.Scalars['Latitude']['output'] | null, longitude: Types.Scalars['Longitude']['output'] | null }, application: { homeDetails: { physicalDetails: { bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, bathroomsFull: Types.Scalars['SafeInt']['output'] | null, bathroomsHalf: Types.Scalars['SafeInt']['output'] | null, sqFt: Types.Scalars['SafeInt']['output'] | null } | null } | null } | null }
        | { __typename: 'InitializingTransaction', id: Types.Scalars['ID']['output'], address: { id: Types.Scalars['ID']['output'], street: Types.Scalars['String']['output'], unitNumber: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'], state: Types.Scalars['String']['output'], postalCode: Types.Scalars['PostalCode']['output'], latitude: Types.Scalars['Latitude']['output'] | null, longitude: Types.Scalars['Longitude']['output'] | null }, application: { homeDetails: { physicalDetails: { bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, bathroomsFull: Types.Scalars['SafeInt']['output'] | null, bathroomsHalf: Types.Scalars['SafeInt']['output'] | null, sqFt: Types.Scalars['SafeInt']['output'] | null } | null } | null } | null }
        | { __typename: 'PreContractTransaction', id: Types.Scalars['ID']['output'], currentStep:
            | { __typename: 'SellerJourneyAssessmentStep', assessmentStatus: Types.SellerAssessmentJourneyStatus | null }
            | { __typename: 'SellerJourneyContractSigningStep', contractStatus: Types.SellerContractJourneyStatus | null }
            | { __typename: 'SellerJourneyUnderwritingStep' }
           | null, address: { id: Types.Scalars['ID']['output'], street: Types.Scalars['String']['output'], unitNumber: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'], state: Types.Scalars['String']['output'], postalCode: Types.Scalars['PostalCode']['output'], latitude: Types.Scalars['Latitude']['output'] | null, longitude: Types.Scalars['Longitude']['output'] | null }, application: { homeDetails: { physicalDetails: { bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, bathroomsFull: Types.Scalars['SafeInt']['output'] | null, bathroomsHalf: Types.Scalars['SafeInt']['output'] | null, sqFt: Types.Scalars['SafeInt']['output'] | null } | null } | null } | null }
        | { __typename: 'WithdrawnTransaction', id: Types.Scalars['ID']['output'], address: { id: Types.Scalars['ID']['output'], street: Types.Scalars['String']['output'], unitNumber: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'], state: Types.Scalars['String']['output'], postalCode: Types.Scalars['PostalCode']['output'], latitude: Types.Scalars['Latitude']['output'] | null, longitude: Types.Scalars['Longitude']['output'] | null }, application: { homeDetails: { physicalDetails: { bedrooms: Types.Scalars['SafeInt']['output'] | null, bathrooms: Types.Scalars['Float']['output'] | null, bathroomsFull: Types.Scalars['SafeInt']['output'] | null, bathroomsHalf: Types.Scalars['SafeInt']['output'] | null, sqFt: Types.Scalars['SafeInt']['output'] | null } | null } | null } | null }
      > } } };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}

export const HubSellerTransactionsDocument = /*#__PURE__*/ new TypedDocumentString(`
    query HubSellerTransactions {
  customerAccount {
    seller {
      transactions(first: 5) {
        __typename
        id
        address {
          id
          street
          unitNumber
          city
          state
          postalCode
          latitude
          longitude
        }
        application {
          homeDetails {
            physicalDetails {
              bedrooms
              bathrooms
              bathroomsFull
              bathroomsHalf
              sqFt
            }
          }
        }
        ... on PreContractTransaction {
          currentStep {
            __typename
            ... on SellerJourneyAssessmentStep {
              assessmentStatus: status
            }
            ... on SellerJourneyContractSigningStep {
              contractStatus: status
            }
          }
        }
        ... on InContractTransaction {
          currentStep {
            __typename
            ... on SellerJourneyClosingStep {
              closingStatus: status
            }
            ... on SellerJourneyRepricingStep {
              repricingStatus: status
            }
          }
        }
        ... on ClosedTransaction {
          selectedProduct
          homeResale {
            __typename
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<HubSellerTransactionsQuery, HubSellerTransactionsQueryVariables>;